/**
 * Clear Contact Info Action
 *
 * Synchronous action that clears email or phone based on contactType.
 * This is an ACTION (not an actor) - fire-and-forget, no async.
 */

import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Clear Contact Info Action
 *
 * Clears email or phoneNumber from machine context based on contactType.
 * Used when user goes back from OTP input to re-enter their contact info.
 *
 * Example:
 * ```json
 * {
 *   "event": "BACK",
 *   "actions": [{ "type": "clearContactInfo" }],
 *   "target": "contactInput"
 * }
 * ```
 */
export const clearContactInfo: ActionWithMetadata = {
  impl: assign(({ context }) => {
    // If authenticated with phone, clear phoneNumber
    if (context.contactType === "phone") {
      return {
        phoneNumber: undefined,
      };
    }
    // If authenticated with email, clear email
    if (context.contactType === "email") {
      return {
        email: undefined,
      };
    }
    // Default: clear both (shouldn't happen, but defensive)
    return {
      phoneNumber: undefined,
      email: undefined,
    };
  }),
  metadata: {
    displayName: "clearContactInfo",
    description: "Clear email or phone based on contactType from context",
    category: "context",
  },
};
